'use client'

import { ComponentProps, useCallback, useRef } from 'react'
import { Swiper as SwiperType } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

import {
  LmfAnalyticsProvider,
  useLmfAnalyticsOnClick,
  useLmfAnalyticsOnSwipe,
} from '@syconium/little-miss-figgy'

const SwiperWithAnalyticsImpl = ({
  onSlideChangeTransitionEnd,
  ...props
}: ComponentProps<typeof Swiper>) => {
  const lmfAnalyticsOnSwipe = useLmfAnalyticsOnSwipe()

  const lastReportedSlidePositionRef = useRef(1)
  const onSlideChangeTransitionEndWithAnalytics = useCallback(
    (swiper: SwiperType) => {
      const position = swiper.realIndex + 1
      if (Boolean(swiper.swipeDirection) && lastReportedSlidePositionRef.current !== position) {
        lastReportedSlidePositionRef.current = position
        lmfAnalyticsOnSwipe({
          action: swiper.swipeDirection == 'prev' ? 'swipe-left' : 'swipe-right',
          element: 'slide',
          variant: 'default',
          label: `Swiped to slide ${position} of ${swiper.slides.length}`,
        })
      }

      // Run callback sent in props
      onSlideChangeTransitionEnd?.(swiper)
    },
    [lmfAnalyticsOnSwipe, onSlideChangeTransitionEnd]
  )

  return (
    <LmfAnalyticsProvider title='Swiper'>
      <Swiper {...props} onSlideChangeTransitionEnd={onSlideChangeTransitionEndWithAnalytics} />
    </LmfAnalyticsProvider>
  )
}

export const SwiperWithAnalytics = (props: ComponentProps<typeof Swiper>) => {
  return (
    <LmfAnalyticsProvider title='Swiper'>
      <SwiperWithAnalyticsImpl {...props} />
    </LmfAnalyticsProvider>
  )
}

const SwiperSlideWithAnalyticsImpl = ({
  children,
  onClick,
  analyticsLabel,
  analyticsVariant,
  ...props
}: Omit<ComponentProps<typeof SwiperSlide>, 'children'> & {
  analyticsLabel?: string
  analyticsVariant?: string
  children?: React.ReactNode
}) => {
  const onClickWithTracking = useLmfAnalyticsOnClick({
    element: 'slide',
    variations: [analyticsVariant ?? 'default'],
    label: analyticsLabel ?? 'slide',
    href: undefined,
    onClick,
  })
  return (
    <SwiperSlide {...props} onClick={onClickWithTracking}>
      {children}
    </SwiperSlide>
  )
}

export const SwiperSlideWithAnalytics = (
  props: ComponentProps<typeof SwiperSlideWithAnalyticsImpl>
) => {
  return (
    <LmfAnalyticsProvider title='Swiper Slide'>
      <SwiperSlideWithAnalyticsImpl {...props} />
    </LmfAnalyticsProvider>
  )
}
SwiperSlideWithAnalytics.displayName = 'SwiperSlide'
